import { PRIMARY_BUTTON } from "@/constants";
import { useState } from "react";
import { MdOutlineKeyboardDoubleArrowUp } from "react-icons/md";
import { AnimatePresence,motion } from "motion/react";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { IEMRData } from "@/api_framework/api_modals/EMRs";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useClickLogAPI,IClickLogBody } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { XMarkIcon,TrashIcon } from "@heroicons/react/24/outline";

type VaccinationAddOn=NonNullable<IEMRData["vaccination_add_ons"]>[number];

interface VaccinationAddOnsProps {
  vaccinationAddOns?: IEMRData["vaccination_add_ons"];
  // mirrors the treatments suggestion-accept flow (handleAddTreatment(item, source))
  onAddItem: (item: any,source: string) => void;
  customerId?: string;
  petId?: string;
  emrId?: string;
  petName:string;
  onDeleteAddon?: (vaccineId: string) => void;
  addedTreatments?: any[];
}

const VaccinationAddOnsComponent=({
  vaccinationAddOns,
  onAddItem,
  customerId,
  petId,
  emrId,
  onDeleteAddon,
  addedTreatments,
  petName
}: VaccinationAddOnsProps) => {
  // selected bom (dosage) id per vaccine row, keyed by vaccine_id|index
  const [selectedBomByVaccine,setSelectedBomByVaccine]=useState<
    Record<string,string>
  >({});

  const { execute: logAPI }=useClickLogAPI();
  const { mappedClinicId,mappedClinicName }=useAppSelector((state) => state.root.UIStates);
  const userLogInfo=useAppSelector((state) => state.auth?.user?.email);
  const [deletedVaccines,setDeletedVaccines]=useState<string[]>([]);
  const [deleteAddon,setDeleteAddon]=useState<VaccinationAddOn|null>(null);
  const [deleteReason,setDeleteReason]=useState("");
  const [deleteError,setDeleteError]=useState("");

  const addedTreatmentIds = new Set(
    addedTreatments?.map((t) => t?.treatment_id).filter(Boolean) || []
  );

  const visibleAddOns =
    vaccinationAddOns?.filter((addon) => {
      if (deletedVaccines.includes(addon?.vaccine_id || "")) {
        return false;
      }
      const isAlreadyAdded = addon?.boms?.some((bom) =>
        addedTreatmentIds.has(bom?.id || "")
      );
      return !isAlreadyAdded;
    }) || [];
  if (!visibleAddOns||visibleAddOns.length===0) return null;

  const handleAdd=(addon: VaccinationAddOn,key: string) => {
    const boms=addon?.boms||[];
    // auto-pick the only dosage if there's just one, else use the selected one
    const selectedId=
      selectedBomByVaccine[key]||(boms.length===1? boms[0]?.id:undefined);
    const bom=boms.find((b) => b?.id===selectedId);
    if (!bom?.id) return;

    // same item shape the treatments search/suggestion flow feeds into
    // handleAddTreatment: treatment_id <- vetic_variant_id, tag #bom.
    // coupon_id rides along; the backend also re-derives it from the add-on.
    onAddItem(
      {
        name: bom.name,
        vetic_variant_id: bom.id,
        item_tag: "#bom",
        coupon_id: addon?.coupon_id??null,
      },
      "vaccination_add_on"
    );
  };
  const handleDeleteClick=(addon: VaccinationAddOn) => { setDeleteAddon(addon); setDeleteReason(""); setDeleteError(""); };

  return (
    <div className="bg-[#FFFFFF] rounded-xl p-[10px] mt-2">
      <p className="text-[#1F1F23] font-semibold text-sm mb-2 px-1">
        Vaccinations for {petName}
      </p>
      <AnimatePresence initial={false}>
        {visibleAddOns.map((addon,idx) => {
          const key=addon?.vaccine_id||String(idx);
          const boms=addon?.boms||[];
          const hasSelection=
            Boolean(selectedBomByVaccine[key])||boms.length===1;

          return (
            <motion.div
              key={key}
              initial={{ opacity: 0,y: -10,scale: 0.98 }}
              animate={{ opacity: 1,y: 0,scale: 1 }}
              exit={{ opacity: 0,y: -10,scale: 0.95 }}
              transition={{ duration: 0.55,ease: "easeInOut" }}
              className="py-2 px-3 border border-b flex flex-col gap-3 bg-white rounded-xl mb-2 sm:flex-row sm:justify-between sm:items-center"
            >
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-2/4 sm:gap-3">
                <p className="text-[#1F1F23] font-medium text-sm">
                  {addon?.vaccine_name}
                </p>
                {addon?.is_overdue&&(
                  <span className="bg-[#FFF7F4] text-[#FF540B] text-xs font-medium rounded-full px-2 py-1 whitespace-nowrap">
                    Overdue
                  </span>
                )}
                {addon?.due_date&&(
                  <span className="text-[#6C727F] text-xs">{addon.due_date}</span>
                )}
              </div>

              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap sm:gap-4">
                <div className="w-full sm:min-w-[340px]">
                  <NonSearchableSelectMenu
                    register={{} as any}
                    fieldError={undefined}
                    errorMessages={[]}
                    placeholder="Select vaccination dosage"
                    showDropdownIcon={true}
                    listBoxClassName="min-w-full w-max max-w-[420px]"
                    defaultSelectId={selectedBomByVaccine[key]||(boms.length===1? boms[0]?.id:"")}
                    selectItems={boms.map((bom) => ({
                      id: bom?.id??"",
                      title: bom?.name??"",
                    }))}
                    onSelectItem={(item) =>
                      setSelectedBomByVaccine((prev) => ({
                        ...prev,
                        [key]: item?.id??"",
                      }))
                    }
                  />
                </div>

                <button
                  className={`${PRIMARY_BUTTON} flex-1 disabled:opacity-50 disabled:cursor-not-allowed sm:flex-none`}
                  disabled={!boms.length||!hasSelection}
                  onClick={() => handleAdd(addon,key)}
                >
                  Add Item <MdOutlineKeyboardDoubleArrowUp className="h-4 w-4" />
                </button>
                <button className="w-9 h-9 shrink-0 flex items-center justify-center bg-[#F2F2F2] rounded-[10px] hover:bg-red-50 transition-colors" type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteClick(addon); }} title="Delete Suggestion"
                >
                  <TrashIcon className="h-5 w-5 text-[#ED0101]" />
                </button>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
      {deleteAddon&&(
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div className="fixed inset-0 w-screen h-screen bg-gray-800 opacity-75 transition-opacity"
            onClick={() => {
              setDeleteAddon(null);
              setDeleteReason("");
              setDeleteError("");
            }}
          />
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left border border-gray-200 transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 w-full overflow-hidden z-10">
              <h3 className="text-base font-semibold leading-6 text-gray-900 flex justify-between items-center mb-4">
                <span>Delete Vaccine Recommendation</span>
                <XMarkIcon className="w-4 h-4 cursor-pointer text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setDeleteAddon(null);
                    setDeleteReason("");
                    setDeleteError("");
                  }}
                />
              </h3>
              <div className="mt-2">
                <span className="text-xs">
                  <p className="text-red-500 text-sm font-medium pb-4">
                    You are deleting recommendation <strong>{deleteAddon?.vaccine_name}</strong>. Please enter a reason to continue.
                  </p>
                  <textarea value={deleteReason}
                    onChange={(e) => {
                      setDeleteReason(e.target.value);
                      if (e.target.value.trim()) setDeleteError("");
                    }}
                    rows={2} placeholder="Enter reason for deleting this suggestion"
                    className="w-full p-2 border border-gray-300 rounded-md text-sm outline-none"
                    autoFocus
                  />
                  {deleteError&&(
                    <p className="text-xs text-red-500 mt-1 font-medium">{deleteError}</p>
                  )}
                </span>
              </div>
              <div className="mt-5 left-0 sm:mt-4 sm:flex items-center sm:flex-row-reverse gap-1">
                <button
                  type="button"
                  disabled={!deleteReason.trim()}
                  onClick={() => {
                    const reason=deleteReason.trim();
                    if (!reason) {
                      setDeleteError("Reason is required to delete recommendation");
                      return;
                    }

                    const logBody: IClickLogBody={
                      event_type: "EMR_ITEM_DELETED",
                      scr_id: "EMR",
                      client_id: customerId,
                      emr_id: emrId,
                      pet_id: petId,
                      click_on_search_item: deleteAddon?.vaccine_name,
                      event_action: reason,
                      clinic_id: mappedClinicId,
                      clinic_name: mappedClinicName,
                      vms_user_id: userLogInfo,
                      timestamp: Date.now(),
                    };
                    logAPI(logBody);

                    const vaccineId = deleteAddon?.vaccine_id || "";
                    onDeleteAddon?.(vaccineId);
                    setDeletedVaccines((prev) => [...prev,vaccineId]);
                    setDeleteAddon(null);
                    setDeleteReason("");
                    setDeleteError("");
                  }}
                  className="inline-flex w-full justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:bg-gray-400 sm:ml-3 sm:w-auto bg-red-600 hover:bg-red-500"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteAddon(null);
                    setDeleteReason("");
                    setDeleteError("");
                  }}
                  className="mt-3 flex items-center w-full disabled:bg-gray-400 justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VaccinationAddOnsComponent;
